import React, { useEffect, useState } from "react";
import { Grid, Pagination, Box, IconButton, Divider, Chip, Button } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { fetchAllProducts } from "../../../State/customer/productSlice";
import ProductCard from "../Product/ProductCard";
import api from "../../../config/Api";
import { HomeBanner } from "../../../types";
import SEO from '../../../component/SEO/SEO';

const shuffleProducts = <T,>(items: T[] = []): T[] => {
  const shuffled = [...items];

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

const Home = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { products, totalPages } = useAppSelector((state) => state.product);

  // State Hooks
  const [slides, setSlides] = useState<HomeBanner[]>([]);
  const [topSellingProducts, setTopSellingProducts] = useState<any[]>([]);
  const [sponsoredProducts, setSponsoredProducts] = useState<any[]>([]);
  const [deals, setDeals] = useState<any[]>([]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [page, setPage] = useState(0);
  const itemsPerPage = 28; // Set to display 28 products per page

  // Track expanded/collapsed state for sections by key
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleExpand = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  // 1. Fetch Dynamic Banners, Top Selling, Sponsored & Deals safely
  useEffect(() => {
    const fetchHomeContent = async () => {
      // Headers optional or empty for public data
      const token = localStorage.getItem("jwt");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        const [slidesRes, topSellingRes, sponsoredRes, dealsRes] = await Promise.allSettled([
          api.get("/home/banners/slides"),
          api.get("/products/top-selling"),
          api.get("/products/sponsored"),
          api.get("/products/deals") // <--- Updated from /api/admin/deals
        ]);

        setSlides(slidesRes.status === "fulfilled" ? slidesRes.value.data || [] : []);

        setTopSellingProducts(
          shuffleProducts(
            topSellingRes.status === "fulfilled"
              ? topSellingRes.value.data || []
              : []
          )
        );

        setSponsoredProducts(
          shuffleProducts(
            sponsoredRes.status === "fulfilled"
              ? sponsoredRes.value.data || []
              : []
          )
        );

        setDeals(
          shuffleProducts(
            dealsRes.status === "fulfilled"
              ? (dealsRes.value.data || []).map((deal: any) => ({
                ...deal,
                products: shuffleProducts(deal.products || []),
              }))
              : []
          )
        );

      } catch (error) {
        console.error("Failed to load home section data:", error);
      }
    };

    fetchHomeContent();
  }, []);

  // 2. Fetch Catalog Products when Page Changes (28 products per page)
  useEffect(() => {
    dispatch(
      fetchAllProducts({
        pageNumber: page,
        pageSize: itemsPerPage,
      })
    );
  }, [dispatch, page]);

  // 3. Hero Carousel Timer
  useEffect(() => {
    if (slides.length === 0) return;
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(slideInterval);
  }, [slides]);

  const handleNextSlide = () => {
    if (slides.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }
  };

  const handlePrevSlide = () => {
    if (slides.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBannerClick = (targetUrl?: string) => {
    if (targetUrl) {
      if (targetUrl.startsWith("http")) {
        window.location.href = targetUrl;
      } else {
        navigate(targetUrl);
      }
    }
  };

  /**
   * Helper component to render products horizontally scrollable or expanded in a grid.
   */
  const RenderProductSectionList = ({
    items,
    sectionKey,
    initialVisibleCount = 6,
  }: {
    items: any[];
    sectionKey: string;
    initialVisibleCount?: number;
  }) => {
    const isExpanded = !!expandedSections[sectionKey];

    if (isExpanded) {
      // Expanded Grid Layout
      return (
        <div>
          <Grid container spacing={2}>
            {items.map((product: any) => (
              <Grid key={`${sectionKey}-${product.id}`} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                <ProductCard item={product} />
              </Grid>
            ))}
          </Grid>
          {items.length > initialVisibleCount && (
            <Box className="flex justify-center mt-4">
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => toggleExpand(sectionKey)}
                endIcon={<KeyboardArrowUp />}
              >
                Show Less
              </Button>
            </Box>
          )}
        </div>
      );
    }

    // Default Horizontal Scrollable Layout
    return (
      <div>
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-300">
          {items.map((product: any) => (
            <div
              key={`${sectionKey}-${product.id}`}
              className="min-w-[180px] max-w-[200px] flex-shrink-0"
            >
              <ProductCard item={product} />
            </div>
          ))}
        </div>
        {items.length > initialVisibleCount && (
          <Box className="flex justify-center mt-2">
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => toggleExpand(sectionKey)}
              endIcon={<KeyboardArrowDown />}
            >
              Show More
            </Button>
          </Box>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">

      <SEO
        title="Power Tools & Equipment Online"
        description="Shop power tools and equipment online at Power Tools. Explore our product range, compare prices and order online."
        canonicalUrl="https://powertoolsltd.co.ke/"
      />

      {/* HERO CAROUSEL */}
      {slides.length > 0 && (
        <section className="relative w-full h-48 sm:h-72 md:h-96 rounded-xl overflow-hidden shadow-md group">
          {slides.map((slide, index) => (
            <div
              key={slide.id || index}
              onClick={() => handleBannerClick(slide.targetUrl)}
              className={`absolute inset-0 cursor-pointer transition-opacity duration-700 ease-in-out ${index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
            >
              <img
                src={slide.imageUrl}
                alt={slide.title || "Slide"}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex flex-col justify-end p-6 sm:p-10 text-white">
                {slide.title && <h2 className="text-xl sm:text-3xl font-bold">{slide.title}</h2>}
                {slide.subtitle && <p className="text-sm sm:text-base mt-1 text-gray-200">{slide.subtitle}</p>}
                {slide.promoCode && (
                  <span className="mt-2 w-fit text-xs bg-primary-color text-white font-semibold px-3 py-1 rounded">
                    Use Code: {slide.promoCode}
                  </span>
                )}
              </div>
            </div>
          ))}

          {slides.length > 1 && (
            <>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevSlide();
                }}
                className="!absolute left-3 top-1/2 -translate-y-1/2 z-20 !bg-white/70 hover:!bg-white text-gray-800"
                size="small"
              >
                <ArrowBackIos className="ml-1" fontSize="small" />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextSlide();
                }}
                className="!absolute right-3 top-1/2 -translate-y-1/2 z-20 !bg-white/70 hover:!bg-white text-gray-800"
                size="small"
              >
                <ArrowForwardIos fontSize="small" />
              </IconButton>

              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {slides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentSlide(idx);
                    }}
                    className={`h-2.5 rounded-full transition-all ${currentSlide === idx ? "w-8 bg-white" : "w-2.5 bg-white/50"
                      }`}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* 1. TOP SELLING PRODUCTS */}
      {topSellingProducts.length > 0 && (
        <section className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">🔥 Top Selling Products</h2>
              <p className="text-xs text-gray-500">Most popular choices among shoppers</p>
            </div>
            <span className="text-xs bg-red-100 text-red-700 font-semibold px-2.5 py-0.5 rounded">
              Best Sellers
            </span>
          </div>

          <RenderProductSectionList
            items={topSellingProducts}
            sectionKey="top-selling"
          />
        </section>
      )}

      {/* 2. SPONSORED PRODUCTS */}
      {sponsoredProducts.length > 0 && (
        <section className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">⭐ Sponsored Products</h2>
              <p className="text-xs text-gray-500">Promoted items you might like</p>
            </div>
            <span className="text-xs bg-amber-100 text-amber-800 font-medium px-2.5 py-0.5 rounded">
              Promoted
            </span>
          </div>

          <RenderProductSectionList
            items={sponsoredProducts}
            sectionKey="sponsored"
          />
        </section>
      )}

      {/* 3. DEALS SECTIONS BY DEAL TITLE */}
      {deals && deals.length > 0 && (
        <div className="space-y-6">
          {deals.map((deal: any, index: number) => {
            const dealKey = `deal-${deal.id || index}`;
            return (
              <section
                key={dealKey}
                className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-orange-200"
              >
                <div className="flex justify-between items-center mb-4 border-b pb-3">
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-orange-600">
                      🏷️ {deal.title || `${deal.discount}% Off Special Deals`}
                    </h2>
                    <p className="text-xs text-gray-500">
                      {deal.category?.categoryName
                        ? `Category: ${deal.category.categoryName}`
                        : "Limited time special discount offer"}
                    </p>
                  </div>
                  {deal.discount && (
                    <Chip
                      label={`${deal.discount}% OFF`}
                      color="error"
                      size="small"
                      className="font-bold"
                    />
                  )}
                </div>

                {deal.products && deal.products.length > 0 ? (
                  <RenderProductSectionList
                    items={deal.products}
                    sectionKey={dealKey}
                  />
                ) : (
                  <p className="text-sm text-gray-400 italic">
                    No specific products assigned under this deal yet.
                  </p>
                )}
              </section>
            );
          })}
        </div>
      )}

      <Divider variant="middle" />

      {/* 4. EXPLORE ALL PRODUCTS */}
      <section className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="mb-6 border-b pb-3 flex justify-between items-center">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Explore All Products</h2>
            <p className="text-sm text-gray-500">Browse through our extensive store collection</p>
          </div>
          <span className="text-xs text-gray-500 font-medium">
            Showing up to 28 per page
          </span>
        </div>

        <Grid container spacing={2}>
          {products?.map((product: any) => (
            <Grid key={product.id} size={{ xs: 6, sm: 6, md: 4, lg: 3 }}>
              <ProductCard item={product} />
            </Grid>
          ))}
        </Grid>

        {totalPages > 1 && (
          <Box className="flex flex-col sm:flex-row items-center justify-between mt-10 gap-4">
            <Button
              variant="outlined"
              disabled={page === 0}
              onClick={() => handlePageChange({} as React.ChangeEvent<unknown>, page)}
            >
              Previous Page
            </Button>

            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />

            <Button
              variant="outlined"
              disabled={page + 1 >= totalPages}
              onClick={() => handlePageChange({} as React.ChangeEvent<unknown>, page + 2)}
            >
              Next Page
            </Button>
          </Box>
        )}
      </section>
    </div>
  );
};

export default Home;